import { useId, useMemo, useState } from 'react'
import { useLang } from '../i18n'
import { anchorNameKey } from '../lib/anchors'
import type { ArtefactAnchor } from '../lib/artefact-anchors'

/**
 * The anchor picker of the link windows (AB#350) — the slides LinkPopover's "Anchor" kind and the
 * prose Link dialog's: every anchor of the artefact (lib/artefact-anchors.ts), searchable by name,
 * grouped by part. Each row says the name, where it sits (§ number in prose, "Slide n" in a deck)
 * and a short excerpt of the anchored text, or the anchored element's kind. One radio group per
 * part, so the keyboard walks it like any form.
 */
export interface AnchorPickerProps {
  /** null while the artefact is being read. */
  anchors: readonly ArtefactAnchor[] | null
  /** Titles of the parts that could not be read. */
  failed?: readonly string[]
  /** The picked anchor's id. */
  value: string | null
  onPick: (anchorId: string) => void
  /** The part open here: its group is said to be "this part". */
  currentRoom?: string
}

/** How an anchored element's kind reads, by key. */
const ELEMENT_KIND_KEY = {
  text: 'an_el_text',
  shape: 'an_el_shape',
  image: 'an_el_image',
  table: 'an_el_table',
  icon: 'an_el_icon',
  group: 'an_el_group',
} as const

export function AnchorPicker({ anchors, failed = [], value, onPick, currentRoom }: AnchorPickerProps) {
  const { t } = useLang()
  const uid = useId()
  const [query, setQuery] = useState('')

  const groups = useMemo(() => {
    const q = anchorNameKey(query)
    const out: Array<{ room: string; title: string; items: ArtefactAnchor[] }> = []
    for (const a of anchors ?? []) {
      if (q && !anchorNameKey(a.name).includes(q)) continue
      let g = out.find((x) => x.room === a.room)
      if (!g) out.push((g = { room: a.room, title: a.partTitle, items: [] }))
      g.items.push(a)
    }
    return out
  }, [anchors, query])

  /** Where an anchor sits: "§ 3.2", "Slide 4", or nothing (prose before the first heading). */
  const contextOf = (a: ArtefactAnchor): string => {
    if (a.partKind === 'slides') return t('sl_slide_n').replace('{n}', String((a.slideIndex ?? 0) + 1))
    return a.section ? t('an_section').replace('{n}', a.section) : ''
  }
  /** What it is on: the excerpt of its letters, or its element's kind. */
  const whatOf = (a: ArtefactAnchor): string => {
    if (a.on === 'text') return a.excerpt
    const key = ELEMENT_KIND_KEY[a.elementKind as keyof typeof ELEMENT_KIND_KEY]
    return key ? t(key) : ''
  }

  return (
    <div className="anchor-picker">
      <label className="anchor-picker-search">
        {t('an_search')}
        <input type="search" value={query} spellCheck={false} onChange={(e) => setQuery(e.target.value)} />
      </label>
      <div className="anchor-picker-list" aria-live="polite" aria-busy={anchors === null}>
        {anchors === null && <p className="anchor-picker-note">{t('an_loading')}</p>}
        {anchors !== null && anchors.length === 0 && <p className="anchor-picker-note">{t('an_none')}</p>}
        {anchors !== null && anchors.length > 0 && groups.length === 0 && <p className="anchor-picker-note">{t('an_no_match')}</p>}
        {groups.map((g) => (
          <fieldset key={g.room} className="anchor-picker-group">
            <legend>
              {g.title}
              {g.room === currentRoom && <span className="anchor-picker-here"> · {t('an_this_part')}</span>}
            </legend>
            {g.items.map((a) => {
              const context = contextOf(a)
              const what = whatOf(a)
              return (
                <label key={a.id} className={`anchor-picker-row${value === a.id ? ' on' : ''}`}>
                  <input type="radio" name={`${uid}-anchor`} value={a.id} checked={value === a.id} onChange={() => onPick(a.id)} />
                  <span className="anchor-picker-name">{a.name}</span>
                  {(context || what) && (
                    <span className="anchor-picker-meta">
                      {context}
                      {context && what ? ' · ' : ''}
                      {what && <span className="anchor-picker-excerpt">{what}</span>}
                    </span>
                  )}
                </label>
              )
            })}
          </fieldset>
        ))}
      </div>
      {failed.length > 0 && <p className="anchor-picker-note">{t('an_failed').replace('{parts}', failed.join(', '))}</p>}
    </div>
  )
}
